import {ThunkAction, ThunkDispatch} from 'redux-thunk';
import hotelsDescblockApi from '@stencil/hotels-descblock-api';
import {hotelsPaymentsApi} from '@stencil/hotels-payments-api';
import hotelsPolicyApi from '@stencil/hotels-policy-api';
import {
  updateDescblockData,
  updatePaymentsData,
  openPaymentModule,
  setDescblockLoading,
  updateCountryCodes,
  updateHotelsPolicy,
  updateOffersFromHermes,
} from './hotels-review-actions';
import {AnyAction} from 'redux';
import {AppState} from '../../../../verticals/hotels-dweb/src/shared/store';
import countryCodesApi from '@stencil/country-codes-api';
import hotelsReviewOffersApi from '@stencil/hotels-review-offers-api';
import {pushga} from 'ga-utils';
import {gaConfig} from '@stencil/hotels-config';
import dateFormat from 'dateformat';

export const fetchDescblockData = (
  errCallback,
  successCallback,
  flavour = '',
): ThunkAction<void, AppState, {}, AnyAction> => {
  return (dispatch: ThunkDispatch<AppState, {}, AnyAction>, getState: () => AppState) => {
    let data = {};
    const {common, review} = getState(); // use relselect
    const {descblockInput} = review;
    const {checkin, checkout, roomString} = common;
    // use async and await
    dispatch(setDescblockLoading(true));
    hotelsDescblockApi({
      checkin: checkin.date,
      checkout: checkout.date,
      input: descblockInput,
      flavour,
    })
      .then(response => {
        if (response.success) {
          dispatch(updateDescblockData({...response, loading: false}));
          successCallback && successCallback();
        } else {
          pushga({
            event: gaConfig.HotelsDwebGenericEvent,
            eventCategory: gaConfig.ReviewGAEvents.category_reviewPageClicks,
            eventAction: gaConfig.ReviewGAEvents.action_descblockFailed,
          });
          errCallback && errCallback();
        }
      })
      .catch(err => {
        pushga({
          event: gaConfig.HotelsDwebGenericEvent,
          eventCategory: gaConfig.ReviewGAEvents.category_reviewPageClicks,
          eventAction: gaConfig.ReviewGAEvents.action_descblockFailed,
        });
        errCallback && errCallback();
      });
    // }
  };
};

export const hotelsPaymentCall = (
  errCallback,
  flavour = '',
  paymentSuccessCallback = () => {},
): ThunkAction<void, AppState, {}, AnyAction> => {
  return (dispatch: ThunkDispatch<AppState, {}, AnyAction>, getState: () => AppState) => {
    let data = {};
    const {common, details, review, user, travellerDetails} = getState(); // use relselect
    const {formData} = travellerDetails;
    const {userInfo} = user;
    const {descblockInput, descblockResponse, useGoCash, earlycheckin, donate} = review;
    const {checkin, checkout, roomString} = common;
    // use async and await
    dispatch(updatePaymentsData({loading: true}));
    hotelsPaymentsApi({
      checkin: checkin.date,
      checkout: checkout.date,
      descblockResponse,
      descblockInput,
      roomString,
      travellerInfo: formData,
      userInfo,
      useGoCash,
      earlycheckin,
      flavour,
      donateCharity: donate,
    })
      .then((response: object) => {
        if (response.success) {
          dispatch(updatePaymentsData({...response, loading: false}));
          dispatch(openPaymentModule());
          paymentSuccessCallback();
        } else {
          errCallback && errCallback(response.message);
          dispatch(updatePaymentsData({loading: false}));
        }
      })
      .catch(err => {
        errCallback && errCallback(err.message);
        dispatch(updatePaymentsData({loading: false}));
      });
    // }
  };
};

export const fetchCountryCodes = (): ThunkAction<void, AppState, {}, AnyAction> => {
  return async (dispatch: ThunkDispatch<AppState, {}, AnyAction>) => {
    const defaultValues = [{name: 'India', code: '91'}];

    try {
      const response = await countryCodesApi();
      if (response.data) dispatch(updateCountryCodes(response.data));
      else dispatch(updateCountryCodes(defaultValues));
    } catch {
      dispatch(updateCountryCodes(defaultValues));
    }
  };
};

export const fetchPolicies = (): ThunkAction<void, AppState, {}, AnyAction> => {
  return async (dispatch: ThunkDispatch<AppState, {}, AnyAction>, getState: () => AppState) => {
    const {review} = getState();
    const {
      fwdParams,
      rpc: roomPriceCode,
      v: vendor,
      pay_mode: inputPaymode,
      rtc: roomTypeCode,
      hc: hotelCode,
      paxString,
      strQry,
      hOfferCode,
    } = review.descblockInput;

    const {pay_mode: responsePayMode = ''} = review?.descblockResponse?.cdata || {};
    const activePaymode = responsePayMode || inputPaymode;

    const stringQuerySplit = strQry.length ? strQry.split('-') : null;

    const cancellationPolicyProps = {
      forwardParams: JSON.parse(fwdParams),
      roomPriceCode,
      offercode: hOfferCode,
      vendor,
      paymode: activePaymode,
      roomTypeCode,
      checkinYYYYMMDD: stringQuerySplit[2],
      checkoutYYYYMMDD: stringQuerySplit[3],
      cityCode: stringQuerySplit[1],
      hotelCode,
      paxString,
    };
    dispatch(updateHotelsPolicy({loading: true, error: false}));
    const [fetchData, error] = await hotelsPolicyApi(cancellationPolicyProps);
    if (fetchData) dispatch(updateHotelsPolicy({...fetchData, loading: false, error: false}));
    else if (!!error) dispatch(updateHotelsPolicy({loading: false, error: true}));
  };
};

export const fetchViewAllOffers = (
  errorCallback,
  flavour = 'mobile',
): ThunkAction<void, AppState, {}, AnyAction> => {
  return async (dispatch: ThunkDispatch<AppState, {}, AnyAction>, getState: () => AppState) => {
    const {review, common} = getState();
    const {descblockInput, descblockResponse, offers} = review;
    const {uph = []} = offers;
    const {checkin, checkout, roomString} = common;
    const ci = dateFormat(checkin.date, 'yyyymmdd');
    const co = dateFormat(checkout.date, 'yyyymmdd');
    const {cdata = {}, promocode = ''} = descblockResponse;

    if (!Object.keys(cdata).length) return;

    const {
      hotel_country_name = '',
      pay_mode,
      is_akb,
      chain_code = '',
      farebreakup = {},
      vcid = '',
      vhid = '',
      promo_applied = 0,
    } = cdata;
    const {
      subtotal = 0,
      netamt = 0,
      goibibo_bookingcharges = 0,
      TotalTaxCharges_without_bookingcharges = 0,
      hcp = {},
    } = farebreakup;
    const {rpc = '', rtc = '', v = ''} = descblockInput;

    const {total = 0, net = 0, tax = 0} = hcp;

    const query = {
      country: hotel_country_name,
      // sb: slotDetail ? 1 : 0,
      sb: 0, // to be checked later
      pa: promo_applied ? promocode : '',
      room_list: [
        {
          rtc,
          rpc,
          pm: pay_mode,
          ibp: v,
          is_akb,
          chc: chain_code,
          price_data: {
            vdp: subtotal,
            netamt,
            conv_fee: goibibo_bookingcharges,
            tax: TotalTaxCharges_without_bookingcharges,
            hcp_dis: total,
            hcp_tax: tax,
            hcp_netamt: net,
          },
        },
      ],
      uph,
    };
    try {
      dispatch(updateOffersFromHermes({loading: true}));

      const response = await hotelsReviewOffersApi({
        query,
        vcid,
        ci,
        co,
        roomString,
        vhid,
        channel: '', // to be checked later
        flavour,
      });
      if (response?.promo_list && Array.isArray(response?.promo_list))
        dispatch(updateOffersFromHermes({...response, loading: false}));
      else if (errorCallback) {
        errorCallback();
        dispatch(updateOffersFromHermes({loading: false}));
      }
    } catch (err) {
      console.log('err', err);
      dispatch(updateOffersFromHermes({loading: false}));
      errorCallback();
    }
  };
};
